/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import apiClient, { AxiosRequestConfig } from 'axios';
import localforage from 'localforage';
import { CountiesLatLng } from '@/common/static';

const palmettoBaseURL = import.meta.env.VITE_PALMETTO_ENDPOINT;
const PARCEL_DOWNLOAD_TIMEOUT_MS = 180000;
const useParcelData = (groupName: string) => {
  const [parcelData, setParcelData] = useState<any>(null);
  const [roadData, setRoadData] = useState<any>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const centerLatLon: [number, number] = useMemo(() => {
    if (groupName) {
      return CountiesLatLng[groupName.toLowerCase().trim()];
    }
    return [34.222558, -82.45869028];
  }, [groupName]);

  function isGroupExist(groupName: string) {
    if (groupName) {
      const countyExist = CountiesLatLng[groupName.toLowerCase().trim()];
      return !!(countyExist && countyExist.length);
    }
    return false;
  }

  const getParcelData = async (nonParcelMode: boolean) => {
    if (!isGroupExist(groupName)) return setIsFetching(false);
    setIsFetching(true);
    const parcelData = await localforage.getItem(
      `${groupName.toLowerCase().trim()}parcels.mbtiles`
    );
    const roads = await localforage.getItem(`${groupName.toLowerCase().trim()}tigerroads.geojson`);
    if ((!nonParcelMode && !parcelData) || !roads) {
      setParcelData(null);
      setRoadData(null);
      return setIsFetching(false);
    }
    setParcelData(parcelData);
    setRoadData(roads);
    setIsFetching(false);
    return {
      parcel: parcelData,
      roads: roads,
    };
  };

  const downloadParcel = async (config: AxiosRequestConfig, nonParcelMode: boolean) => {
    setIsFetching(true);
    const baseUrl = `${palmettoBaseURL}/api/damageassessment_pwa`;
    const tileName = `${groupName.toLowerCase().trim()}parcels.mbtiles`;
    const roadName = `${groupName.toLowerCase().trim()}tigerroads.geojson`;

    // Tiles and roads are independent downloads with no shared data -- run them
    // concurrently rather than sequentially to roughly halve total wait time on
    // slow field connections.
    const downloadTiles = async () => {
      try {
        const responseTiles = nonParcelMode
          ? { data: null }
          : await apiClient.get(`${baseUrl}/getTiles?group=${groupName.toLowerCase().trim()}`, {
              ...config,
              timeout: PARCEL_DOWNLOAD_TIMEOUT_MS,
              onDownloadProgress: (progressEvent) => {
                const progress = Math.round(
                  (100 * progressEvent.loaded) / (progressEvent?.total ?? 0)
                );
                setProgress(progress);
              },
            });

        // Save the tile data to localforage
        await localforage.setItem(tileName, responseTiles.data);
        setParcelData(responseTiles.data);
      } catch (error) {
        // Handle errors in downloading or saving parcel tiles
        console.error('Error occurred while downloading parcel tiles', error);
        // Persist null (not a truthy placeholder like '{}') so downstream falsy-checks
        // correctly detect a failed download instead of treating it as cached data.
        if (isGroupExist(groupName)) {
          await localforage.setItem(tileName, null);
          setParcelData(null);
        } else {
          setParcelData(null);
        }
      }
    };

    const downloadRoads = async () => {
      try {
        const responseRoad = await apiClient.get(
          `${baseUrl}/getRoads?group=${groupName.toLowerCase().trim()}`,
          {
            ...config,
            timeout: PARCEL_DOWNLOAD_TIMEOUT_MS,
            onDownloadProgress: (progressEvent) => {
              const progress = Math.round(
                (100 * progressEvent.loaded) / (progressEvent?.total ?? 0)
              );
              setProgress(progress);
            },
          }
        );

        // Save the road data to localforage
        await localforage.setItem(roadName, responseRoad.data);
        setRoadData(responseRoad.data);
      } catch (error) {
        // Handle errors in downloading or saving road data
        console.error('Error occurred while downloading road data', error);

        // Persist null (not a truthy placeholder like '{}') so downstream falsy-checks
        // correctly detect a failed download instead of treating it as cached data.
        // Guarded by isGroupExist for consistency with the tiles branch above.
        if (isGroupExist(groupName)) {
          await localforage.setItem(roadName, null);
        }
        setRoadData(null);
      }
    };

    await Promise.allSettled([downloadTiles(), downloadRoads()]);
    setIsFetching(false);
  };

  return {
    getParcelData,
    downloadParcel,
    parcelData,
    roadData,
    isFetching,
    progress,
    centerLatLon,
  };
};

export default useParcelData;
